import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Plus, Users, Tag, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  superadmin: 'SuperAdmin',
  admin: 'Admin',
  operator: 'Operator',
  technician: 'Texnik',
  accountant: 'Mühasib',
  d2d_agent: 'D2D Agent',
  lawyer: 'Hüquqşunas',
  network_admin: 'NetAdmin',
  system_admin: 'SysAdmin',
};

export default function ProjectMembersDialog({ open, onClose, project, onSaved }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [allUsers, setAllUsers] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const existingUserIds = new Set(project?.member_ids || []);
  const existingGroupIds = new Set(project?.member_groups || []);

  const loadOptions = useCallback(async () => {
    try {
      const [usersRes, groupsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/groups`, { headers }),
      ]);
      setAllUsers(usersRes.data || []);
      setAllGroups(groupsRes.data || []);
    } catch (e) {
      toast.error('Seçimlər yüklənmədi');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      loadOptions();
      setSelectedUserIds(new Set());
      setSelectedGroupIds(new Set());
      setUserSearch('');
      setGroupSearch('');
    }
  }, [open, loadOptions]);

  const toggleUser = (id) => {
    const next = new Set(selectedUserIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedUserIds(next);
  };

  const toggleGroup = (id) => {
    const next = new Set(selectedGroupIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedGroupIds(next);
  };

  const filteredUsers = allUsers
    .filter((u) => !existingUserIds.has(u.id))
    .filter((u) => u.is_active !== false && u.role !== 'superadmin' && !u.is_superadmin)
    .filter((u) => {
      if (!userSearch) return true;
      const q = userSearch.toLowerCase();
      return (
        (u.username || '').toLowerCase().includes(q) ||
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.role || '').toLowerCase().includes(q)
      );
    });

  const filteredGroups = allGroups
    .filter((g) => !existingGroupIds.has(g.id))
    .filter((g) => {
      if (!groupSearch) return true;
      return (g.name || '').toLowerCase().includes(groupSearch.toLowerCase());
    });

  const handleSave = async () => {
    if (selectedUserIds.size === 0 && selectedGroupIds.size === 0) {
      toast.error('Heç bir seçim edilməyib');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/projects/${project.id}/members`,
        {
          user_ids: Array.from(selectedUserIds),
          group_ids: Array.from(selectedGroupIds),
        },
        { headers }
      );
      toast.success('Üzvlər əlavə edildi');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Əlavə edilmədi');
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = selectedUserIds.size + selectedGroupIds.size;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="members-dialog">
        <DialogHeader>
          <DialogTitle>&quot;{project?.name}&quot; — Üzv əlavə et</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="h-4 w-4 mr-2" />
              İstifadəçilər ({selectedUserIds.size})
            </TabsTrigger>
            <TabsTrigger value="groups" data-testid="tab-groups">
              <Tag className="h-4 w-4 mr-2" />
              Qruplar ({selectedGroupIds.size})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-3 mt-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
              <Input
                placeholder="Ad, username, rol axtar..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="pl-9"
                data-testid="user-search"
              />
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1 border rounded-md p-2">
              {filteredUsers.length === 0 && (
                <div className="py-8 text-center text-sm text-zinc-500">
                  Əlavə edilə bilən istifadəçi yoxdur
                </div>
              )}
              {filteredUsers.map((u) => {
                const checked = selectedUserIds.has(u.id);
                return (
                  <label
                    key={u.id}
                    className={`flex items-center gap-3 px-2 py-2 rounded cursor-pointer hover:bg-zinc-50 ${
                      checked ? 'bg-purple-50' : ''
                    }`}
                    data-testid={`user-option-${u.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleUser(u.id)}
                      className="h-4 w-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {u.full_name || u.username}
                      </div>
                      <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
                        <span>@{u.username}</span>
                        <Badge variant="secondary" className="text-xs">
                          {ROLE_LABELS[u.role] || u.role}
                        </Badge>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="groups" className="space-y-3 mt-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
              <Input
                placeholder="Qrup adı axtar..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                className="pl-9"
                data-testid="group-search"
              />
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1 border rounded-md p-2">
              {filteredGroups.length === 0 && (
                <div className="py-8 text-center text-sm text-zinc-500">
                  Əlavə edilə bilən qrup yoxdur
                </div>
              )}
              {filteredGroups.map((g) => {
                const checked = selectedGroupIds.has(g.id);
                return (
                  <label
                    key={g.id}
                    className={`flex items-center gap-3 px-2 py-2 rounded cursor-pointer hover:bg-zinc-50 ${
                      checked ? 'bg-purple-50' : ''
                    }`}
                    data-testid={`group-option-${g.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleGroup(g.id)}
                      className="h-4 w-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{g.name}</div>
                      <div className="text-xs text-zinc-500">
                        Qrup — bütün üzvləri layihəyə daxil edir
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Ləğv et
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || selectedCount === 0}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="save-members-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            {selectedCount > 0 ? `${selectedCount} əlavə et` : 'Əlavə et'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
